import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon, XIcon, SparklesIcon, TrophyIcon, StarIcon, AwardIcon } from 'lucide-react';
interface LessonCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  lessonTitle: string;
  moduleName: string;
  onContinue: () => void;
  pointsEarned?: number;
  isLastInModule?: boolean;
  hasNextLesson?: boolean;
}
export function LessonCompletionModal({
  isOpen,
  onClose,
  lessonTitle,
  moduleName,
  onContinue,
  pointsEarned = 10,
  isLastInModule = false,
  hasNextLesson = true
}: LessonCompletionModalProps) {
  // Calculate bonus points for module completion
  const moduleBonus = isLastInModule ? 25 : 0;
  const totalPoints = pointsEarned + moduleBonus;
  // Determine the button text based on context
  const getButtonText = () => {
    if (isLastInModule && hasNextLesson) {
      return 'Treci la următorul modul';
    } else if (isLastInModule && !hasNextLesson) {
      return 'Treci la următorul training';
    } else {
      return 'Următoarea lecție';
    }
  };
  // Determine the congratulations message
  const getCongratulatoryMessage = () => {
    if (isLastInModule) {
      return `Ai finalizat cu succes modulul "${moduleName}"! 🎉`;
    }
    return 'Excelent! Continui să faci progrese remarcabile în parcursul tău de dezvoltare profesională.';
  };
  return <AnimatePresence data-id="element-1205">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-1206" />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4" data-id="element-1207">
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 20
        }} transition={{
          type: 'spring',
          duration: 0.5
        }} className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden relative" data-id="element-1208">
              {/* Close button */}
              <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-10" data-id="element-1209">
                <XIcon className="w-5 h-5 text-gray-500" data-id="element-1210" />
              </button>

              {/* Confetti background effect */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1211">
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.2
            }} className="absolute inset-0" style={{
              background: isLastInModule ? 'radial-gradient(circle at 50% 50%, rgba(33, 163, 102, 0.15) 0%, transparent 70%)' : 'radial-gradient(circle at 50% 50%, rgba(53, 123, 235, 0.1) 0%, transparent 70%)'
            }} data-id="element-1212" />
                {/* Animated sparkles */}
                {[...Array(isLastInModule ? 12 : 8)].map((_, i) => <motion.div key={i} initial={{
              opacity: 0,
              scale: 0,
              rotate: 0
            }} animate={{
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
              rotate: [0, 180, 360],
              x: [0, Math.random() * 200 - 100],
              y: [0, Math.random() * 200 - 100]
            }} transition={{
              duration: 2,
              delay: i * 0.1,
              repeat: Infinity,
              repeatDelay: 1
            }} className="absolute" style={{
              left: `${20 + Math.random() * 60}%`,
              top: `${20 + Math.random() * 60}%`
            }} data-id="element-1213">
                    <SparklesIcon className="w-4 h-4 text-yellow-400" data-id="element-1214" />
                  </motion.div>)}
              </div>

              {/* Content */}
              <div className="relative p-8 text-center" data-id="element-1215">
                {/* Success Icon */}
                <motion.div initial={{
              scale: 0,
              rotate: -180
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              type: 'spring',
              stiffness: 200,
              damping: 15,
              delay: 0.2
            }} className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center shadow-xl ${isLastInModule ? 'bg-gradient-to-br from-emerald-400 via-green-500 to-teal-600' : 'bg-gradient-to-br from-emerald-400 to-green-600'}`} data-id="element-1216">
                  {isLastInModule ? <AwardIcon className="w-14 h-14 text-white" data-id="element-1217" /> : <CheckCircleIcon className="w-14 h-14 text-white" data-id="element-1218" />}
                </motion.div>

                {/* Title */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} data-id="element-1219">
                  <div className="flex items-center justify-center space-x-2 mb-3" data-id="element-1220">
                    <TrophyIcon className="w-6 h-6 text-yellow-500" data-id="element-1221" />
                    <h2 className="text-3xl font-bold text-[#132C53]" data-id="element-1222">
                      {isLastInModule ? 'Modul Finalizat!' : 'Felicitări!'}
                    </h2>
                    <TrophyIcon className="w-6 h-6 text-yellow-500" data-id="element-1223" />
                  </div>
                  <p className="text-lg text-gray-600 mb-2" data-id="element-1224">
                    {isLastInModule ? 'Ai finalizat cu succes modulul' : 'Ai finalizat cu succes lecția'}
                  </p>
                  <p className="text-xl font-bold text-[#357BEB] mb-6" data-id="element-1225">
                    "{lessonTitle}"
                  </p>
                </motion.div>

                {/* Points Earned */}
                <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.4,
              type: 'spring'
            }} className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 mb-6 border-2 border-amber-200" data-id="element-1226">
                  <div className="flex items-center justify-center space-x-3 mb-2" data-id="element-1227">
                    <StarIcon className="w-8 h-8 text-amber-500 fill-amber-500" data-id="element-1228" />
                    <div data-id="element-1229">
                      <p className="text-sm text-gray-600 mb-1" data-id="element-1230">Ai câștigat</p>
                      <p className="text-4xl font-black text-amber-600" data-id="element-1231">
                        +{totalPoints}
                      </p>
                    </div>
                    <StarIcon className="w-8 h-8 text-amber-500 fill-amber-500" data-id="element-1232" />
                  </div>
                  {isLastInModule ? <div className="space-y-1" data-id="element-1233">
                      <p className="text-sm font-semibold text-amber-700" data-id="element-1234">
                        {pointsEarned} puncte (lecție) + {moduleBonus} puncte
                        bonus (modul)! 🎉
                      </p>
                      <p className="text-xs text-amber-600" data-id="element-1235">
                        Bonus special pentru finalizarea modulului!
                      </p>
                    </div> : <p className="text-sm font-semibold text-amber-700" data-id="element-1236">
                      puncte pentru finalizarea lecției! 🎉
                    </p>}
                </motion.div>

                {/* Stats/Encouragement */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5
            }} className={`rounded-2xl p-6 mb-6 border-2 ${isLastInModule ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200' : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100'}`} data-id="element-1237">
                  <p className="text-gray-700 leading-relaxed" data-id="element-1238">
                    {getCongratulatoryMessage()} Fiecare pas te apropie de
                    obiectivele tale! 🚀
                  </p>
                </motion.div>

                {/* Action Buttons */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.6
            }} className="flex flex-col sm:flex-row gap-3" data-id="element-1239">
                  <button onClick={onClose} className="flex-1 px-6 py-3 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors" data-id="element-1240">
                    Revizuiește lecția
                  </button>
                  <button onClick={onContinue} className={`flex-1 px-6 py-3 rounded-xl font-semibold text-white transition-all shadow-lg hover:shadow-xl flex items-center justify-center space-x-2 ${isLastInModule ? 'bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700' : 'bg-gradient-to-r from-[#357BEB] to-[#2868d8] hover:from-[#2868d8] hover:to-[#1e5bb8]'}`} data-id="element-1241">
                    <span data-id="element-1242">{getButtonText()}</span>
                    <ArrowRightIcon className="w-5 h-5" data-id="element-1243" />
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}